/**
 * CodeMirror <-> Yjs binding with track-changes support.
 *
 * Based on y-codemirror.next's ySync plugin (MIT), extended so that when
 * "track changes" is on, local edits are recorded as suggestions in the same
 * Yjs transaction as the text change. The undo manager here also mirrors
 * y-codemirror's (which isn't exported) but tracks the review maps too, so
 * undoing a tracked edit removes its suggestion.
 */
import { Annotation, EditorSelection, Facet, type ChangeSet, type Extension } from "@codemirror/state";
import { EditorView, ViewPlugin, keymap, type ViewUpdate } from "@codemirror/view";
import * as Y from "yjs";
import { YSyncConfig, ySyncAnnotation, ySyncFacet, yRemoteSelections, yRemoteSelectionsTheme } from "y-codemirror.next";
import type { Awareness } from "y-protocols/awareness";

export const TEXT_KEY = "content";

export interface ReviewUser {
  id: number;
  name: string;
}

export interface ChangeRecord {
  type: "ins" | "del";
  /** Encoded Y.RelativePosition (base64) */
  from?: string;
  to?: string;
  pos?: string;
  text?: string;
  userId: number;
  userName: string;
  ts: number;
}

export interface CollabOptions {
  ytext: Y.Text;
  awareness: Awareness;
  undoManager: Y.UndoManager;
  user: ReviewUser;
  /** Read at edit time, so toggling doesn't require rebuilding the editor. */
  isTracking: () => boolean;
}

export const collabOptions = Facet.define<CollabOptions, CollabOptions>({ combine: (v) => v[v.length - 1] });

export const changesMap = (doc: Y.Doc) => doc.getMap<ChangeRecord>("changes");
export const threadsMap = (doc: Y.Doc) => doc.getMap<Y.Map<unknown>>("threads");

// ---------------------------------------------------------------- relative positions

export function encodeRel(ytext: Y.Text, index: number, assoc = 0): string {
  const rel = Y.createRelativePositionFromTypeIndex(ytext, index, assoc);
  return bytesToB64(Y.encodeRelativePosition(rel));
}

export function decodeRel(doc: Y.Doc, encoded: string | undefined): number | null {
  if (!encoded) return null;
  try {
    const rel = Y.decodeRelativePosition(b64ToBytes(encoded));
    const abs = Y.createAbsolutePositionFromRelativePosition(rel, doc);
    return abs ? abs.index : null;
  } catch {
    return null;
  }
}

function bytesToB64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}
function b64ToBytes(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function newId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

/** Resolved ranges of all tracked insertions, in current-document coordinates. */
export function resolvedInsertions(doc: Y.Doc): Array<{ id: string; from: number; to: number; rec: ChangeRecord }> {
  const out: Array<{ id: string; from: number; to: number; rec: ChangeRecord }> = [];
  changesMap(doc).forEach((rec, id) => {
    if (rec.type !== "ins") return;
    const from = decodeRel(doc, rec.from);
    const to = decodeRel(doc, rec.to);
    if (from === null || to === null) return;
    out.push({ id, from, to, rec });
  });
  return out;
}

// ---------------------------------------------------------------- sync plugin

/** Marks CM transactions that the review code itself dispatches (accept/reject), so they aren't tracked. */
export const reviewAction = Annotation.define<boolean>();

class SyncPlugin {
  conf: YSyncConfig;
  private observer: (event: Y.YTextEvent, tr: Y.Transaction) => void;

  constructor(private view: EditorView) {
    this.conf = view.state.facet(ySyncFacet);
    this.observer = (event, tr) => {
      if (tr.origin === this.conf) return;
      const changes: Array<{ from: number; to: number; insert: string }> = [];
      let pos = 0;
      for (const d of event.delta) {
        if (d.insert != null) changes.push({ from: pos, to: pos, insert: d.insert as string });
        else if (d.delete != null) {
          changes.push({ from: pos, to: pos + d.delete, insert: "" });
          pos += d.delete;
        } else pos += d.retain ?? 0;
      }
      view.dispatch({ changes, annotations: [ySyncAnnotation.of(this.conf)] });
    };
    this.conf.ytext.observe(this.observer);
  }

  update(update: ViewUpdate) {
    if (!update.docChanged) return;
    if (update.transactions.length > 0 && update.transactions[0].annotation(ySyncAnnotation) === this.conf) return;
    const opts = update.state.facet(collabOptions);
    const tracking = opts.isTracking() && !update.transactions.some((t) => t.annotation(reviewAction));
    const changes: TextChange[] = [];
    update.changes.iterChanges((fromA, toA, _fromB, _toB, insert) => changes.push({ from: fromA, to: toA, insert: insert.sliceString(0, insert.length, "\n") }));
    applyChanges(this.conf.ytext, changes, tracking ? opts.user : null, this.conf, (from, to) => update.startState.doc.sliceString(from, to, "\n"));
  }

  destroy() {
    this.conf.ytext.unobserve(this.observer);
  }
}

export interface TextChange {
  from: number;
  to: number;
  insert: string;
}

/**
 * Apply local edits to a Y.Text in one transaction. Changes are in the current
 * document's coordinates, in order and not overlapping; `oldText` slices the
 * document as it was. With trackAs, they're recorded as that person's suggestions.
 */
export function applyChanges(ytext: Y.Text, changes: TextChange[], trackAs: ReviewUser | null, origin: unknown, oldText: (from: number, to: number) => string) {
  const doc = ytext.doc!;
  // Insertion ranges before the change (old-document coordinates), for deciding what a deletion removes.
  const before = trackAs ? resolvedInsertions(doc) : [];
  const delsBefore = new Map<string, number>();
  if (trackAs) {
    changesMap(doc).forEach((rec, id) => {
      if (rec.type !== "del") return;
      const at = decodeRel(doc, rec.pos);
      if (at !== null) delsBefore.set(id, at);
    });
  }

  doc.transact(() => {
    let adj = 0;
    const recorded: Array<{ fromB: number; toB: number; deleted: string; fromA: number; toA: number }> = [];
    for (const { from: fromA, to: toA, insert } of changes) {
      const fromB = fromA + adj;
      const deleted = trackAs && fromA !== toA ? oldText(fromA, toA) : "";
      if (fromA !== toA) ytext.delete(fromB, toA - fromA);
      if (insert.length > 0) ytext.insert(fromB, insert);
      adj += insert.length - (toA - fromA);
      if (trackAs) recorded.push({ fromB, toB: fromB + insert.length, deleted, fromA, toA });
    }
    if (trackAs) recordTrackedChanges(doc, ytext, trackAs, recorded, before, delsBefore);
  }, origin);
}

/**
 * Turn the raw changes of one local transaction into suggestion records.
 * Text deleted from inside a tracked insertion just disappears (like Overleaf);
 * other deleted text is kept on the record so it can be restored on reject.
 */
function recordTrackedChanges(
  doc: Y.Doc,
  ytext: Y.Text,
  user: ReviewUser,
  changes: Array<{ fromB: number; toB: number; deleted: string; fromA: number; toA: number }>,
  insertionsBefore: Array<{ id: string; from: number; to: number; rec: ChangeRecord }>,
  deletionsBefore: Map<string, number>,
) {
  const map = changesMap(doc);
  const ts = Date.now();
  for (const c of changes) {
    if (c.deleted) {
      // Drop the parts of the deleted range that were themselves tracked insertions.
      let kept = "";
      let keptBefore = 0; // kept chars before each old position, for splicing merged text
      const keptUpTo: number[] = [];
      for (let i = c.fromA; i < c.toA; i++) {
        keptUpTo.push(keptBefore);
        const insideIns = insertionsBefore.some((r) => i >= r.from && i < r.to);
        if (!insideIns) {
          kept += c.deleted[i - c.fromA];
          keptBefore++;
        }
      }
      keptUpTo.push(keptBefore);
      if (kept) {
        // Fold in our own deletions that touch or sit inside the removed range.
        let text = kept;
        let mergedInto: string | null = null;
        for (const [id, oldPos] of deletionsBefore) {
          const rec = map.get(id);
          if (!rec || rec.type !== "del" || rec.userId !== user.id) continue;
          if (oldPos < c.fromA || oldPos > c.toA) continue;
          const at = keptUpTo[oldPos - c.fromA];
          text = text.slice(0, at) + (rec.text ?? "") + text.slice(at);
          if (mergedInto) map.delete(id);
          else mergedInto = id;
          deletionsBefore.delete(id);
        }
        const rec: ChangeRecord = { type: "del", pos: encodeRel(ytext, c.fromB), text, userId: user.id, userName: user.name, ts };
        map.set(mergedInto ?? newId(), rec);
      }
    }
    if (c.toB > c.fromB) {
      // Extend an adjacent insertion of ours instead of creating many small ones.
      let extended = false;
      map.forEach((rec, id) => {
        if (extended || rec.type !== "ins" || rec.userId !== user.id) return;
        const from = decodeRel(doc, rec.from);
        const to = decodeRel(doc, rec.to);
        if (from === null || to === null) return;
        if ((c.fromB >= from && c.fromB <= to) || c.toB === from) {
          const newFrom = Math.min(from, c.fromB);
          const newTo = Math.max(to, c.toB);
          map.set(id, { ...rec, from: encodeRel(ytext, newFrom, 0), to: encodeRel(ytext, newTo, -1), ts });
          extended = true;
        }
      });
      if (!extended) {
        map.set(newId(), {
          type: "ins",
          from: encodeRel(ytext, c.fromB, 0),
          to: encodeRel(ytext, c.toB, -1),
          userId: user.id,
          userName: user.name,
          ts,
        });
      }
    }
  }
}

const syncPlugin = ViewPlugin.fromClass(SyncPlugin);

// ---------------------------------------------------------------- undo (mirrors y-codemirror.next's yUndoManager)

class UndoPlugin {
  private conf: YSyncConfig;
  private um: Y.UndoManager;
  private beforeSel: unknown = null;
  private onAdded: (e: { stackItem: { meta: Map<unknown, unknown> }; changedParentTypes: Map<Y.AbstractType<unknown>, unknown> }) => void;
  private onPopped: (e: { stackItem: { meta: Map<unknown, unknown> } }) => void;

  constructor(view: EditorView) {
    this.conf = view.state.facet(ySyncFacet);
    this.um = view.state.facet(collabOptions).undoManager;
    this.onAdded = ({ stackItem, changedParentTypes }) => {
      if (changedParentTypes.has(this.conf.ytext) && this.beforeSel && !stackItem.meta.has(this)) stackItem.meta.set(this, this.beforeSel);
    };
    this.onPopped = ({ stackItem }) => {
      const sel = stackItem.meta.get(this);
      if (!sel) return;
      const range = this.conf.fromYRange(sel as never);
      view.dispatch({ selection: EditorSelection.create([range]), effects: [EditorView.scrollIntoView(range)] });
      this.store(view);
    };
    this.um.on("stack-item-added", this.onAdded as never);
    this.um.on("stack-item-popped", this.onPopped as never);
    this.um.addTrackedOrigin(this.conf);
  }

  private store(view: EditorView) {
    this.beforeSel = this.conf.toYRange(view.state.selection.main);
  }

  update(update: ViewUpdate) {
    if (update.selectionSet && (update.transactions.length === 0 || update.transactions[0].annotation(ySyncAnnotation) !== this.conf)) {
      this.store(update.view);
    }
  }

  destroy() {
    this.um.off("stack-item-added", this.onAdded as never);
    this.um.off("stack-item-popped", this.onPopped as never);
    this.um.removeTrackedOrigin(this.conf);
  }
}

const undoPlugin = ViewPlugin.fromClass(UndoPlugin);

export function undoCommand(view: EditorView) {
  return view.state.facet(collabOptions).undoManager.undo() != null;
}
export function redoCommand(view: EditorView) {
  return view.state.facet(collabOptions).undoManager.redo() != null;
}

export function collab(opts: CollabOptions): Extension {
  const conf = new YSyncConfig(opts.ytext, opts.awareness);
  return [
    collabOptions.of(opts),
    ySyncFacet.of(conf),
    // The undo plugin must come before the sync plugin so it stores the selection first.
    undoPlugin,
    syncPlugin,
    yRemoteSelectionsTheme,
    yRemoteSelections,
    keymap.of([
      { key: "Mod-z", run: undoCommand, preventDefault: true },
      { key: "Mod-y", mac: "Mod-Shift-z", run: redoCommand, preventDefault: true },
      { key: "Mod-Shift-z", run: redoCommand, preventDefault: true },
    ]),
    EditorView.domEventHandlers({
      beforeinput(e, view) {
        if (e.inputType === "historyUndo") return undoCommand(view);
        if (e.inputType === "historyRedo") return redoCommand(view);
        return false;
      },
    }),
  ];
}

/** Map a position through a changeset (used by decoration caches). */
export function mapPos(changes: ChangeSet, pos: number, assoc = -1) {
  return changes.mapPos(pos, assoc);
}
